import { Box, Typography, Tooltip } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import type { ReactNode } from 'react';
import { PanelCard } from './PanelCard';
import { AutoFitText } from './AutoFitText';
import { usePanelTokens } from './tokens';

export interface KpiStatCardProps {
  label: ReactNode;
  value: ReactNode;
  /** Delta text (e.g. "12.4% vs last yr"). */
  delta?: ReactNode;
  /** Direction of the delta — colors + arrow. */
  trend?: 'up' | 'down' | 'flat';
  /** Optional icon node rendered in the accent tile. */
  icon?: ReactNode;
  /** Accent the value with the brand color (e.g. the headline KPI). */
  accentValue?: boolean;
  /** Hover tooltip explaining the metric — what it measures, how it's calculated, what to do. */
  tooltip?: string;
}

/** Plain-number KPI card with a colored delta and an accent icon tile. */
export function KpiStatCard({ label, value, delta, trend = 'flat', icon, accentValue, tooltip }: KpiStatCardProps) {
  const t = usePanelTokens();
  const trendColor = trend === 'up' ? t.positive : trend === 'down' ? t.negative : t.label;
  return (
    <Tooltip title={tooltip ?? ''} placement="top" arrow disableHoverListener={!tooltip}>
    <PanelCard>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.75rem', color: t.label, fontWeight: 500 }}>{label}</Typography>
          <AutoFitText
            maxPx={25.6}
            sx={{
              fontWeight: 800,
              letterSpacing: '-0.02em',
              lineHeight: 1,
              mt: 1,
              color: accentValue ? t.a : t.ink,
            }}
          >
            {value}
          </AutoFitText>
          {delta && (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.3,
                mt: 1.1,
                fontSize: '0.75rem',
                fontWeight: 700,
                color: trendColor,
              }}
            >
              {trend === 'up' && <ArrowUpwardIcon sx={{ fontSize: 13 }} />}
              {trend === 'down' && <ArrowDownwardIcon sx={{ fontSize: 13 }} />}
              {delta}
            </Box>
          )}
        </Box>
        {icon && (
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: '10px',
              bgcolor: t.aSoft,
              color: t.a,
              display: 'grid',
              placeItems: 'center',
              flex: 'none',
              '& svg': { fontSize: 20 },
            }}
          >
            {icon}
          </Box>
        )}
      </Box>
    </PanelCard>
    </Tooltip>
  );
}
